/* vinyl.ae taste engine v1
   On-device personalisation for the personal shelves and the For You feed.
   Signals (views, saves, adds to cart, skips) stay in this browser's localStorage.
   Cards are rendered by the theme itself (sections/vae-cards.liquid) so they match the store. */
(() => {
  'use strict';
  const ctxEl = document.getElementById('vae-ctx');
  if (!ctxEl || !window.fetch || !window.DOMParser) return;
  let ctx;
  try { ctx = JSON.parse(ctxEl.textContent) || {}; } catch (e) { return; }

  const KEY = 'vae_taste_v1';
  const DAY = 864e5;
  const JUNK = /^(gmc-|desc-v|google-|mhm-|free-|judgeme|yotpo|top artists$|pre-?order|sale$|new$|all$)/i;
  const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
  const split = (s) => (Array.isArray(s) ? s : String(s == null ? '' : s).split('|')).map((x) => String(x).trim()).filter(Boolean);
  const num = (x) => Number(x) || 0;
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const HEART = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 20.7l-1.2-1.1C5.4 14.7 2 11.6 2 7.9 2 4.9 4.4 2.5 7.4 2.5c1.7 0 3.4.8 4.6 2.1 1.2-1.3 2.9-2.1 4.6-2.1 3 0 5.4 2.4 5.4 5.4 0 3.7-3.4 6.8-8.8 11.7L12 20.7z"/></svg>';
  const CROSS = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  /* ---------- state ---------- */
  const blank = () => ({ v: 1, t: Date.now(), w: {}, labels: {}, viewed: [], liked: [], hidden: [], seen: [], titles: {} });
  let S = null;
  try { S = JSON.parse(localStorage.getItem(KEY)); } catch (e) { S = null; }
  if (!S || S.v !== 1 || !S.w || typeof S.w !== 'object') S = blank();
  ['viewed', 'liked', 'hidden', 'seen'].forEach((k) => { if (!Array.isArray(S[k])) S[k] = []; });
  ['labels', 'titles'].forEach((k) => { if (!S[k] || typeof S[k] !== 'object') S[k] = {}; });

  // Taste fades slowly (about 3% a day) so the feed follows current interests.
  (() => {
    const days = (Date.now() - (S.t || Date.now())) / DAY;
    if (days < 0.25) return;
    const f = Math.pow(0.97, days);
    Object.keys(S.w).forEach((k) => {
      S.w[k] *= f;
      if (Math.abs(S.w[k]) < 0.05) { delete S.w[k]; delete S.labels[k]; }
    });
    S.t = Date.now();
  })();

  let saveTimer = 0;
  const flush = () => {
    clearTimeout(saveTimer);
    saveTimer = 0;
    const keep = S.viewed.concat(S.liked);
    Object.keys(S.titles).forEach((id) => { if (keep.indexOf(num(id)) === -1) delete S.titles[id]; });
    try { localStorage.setItem(KEY, JSON.stringify(S)); } catch (e) { /* storage blocked or full */ }
  };
  // Coalesce writes, but never delay them by more than 60ms.
  const save = () => { if (!saveTimer) saveTimer = setTimeout(flush, 60); };
  window.addEventListener('pagehide', () => { if (saveTimer) flush(); });
  const pushId = (list, id, max) => {
    id = num(id);
    if (!id) return;
    const i = list.indexOf(id);
    if (i > -1) list.splice(i, 1);
    list.unshift(id);
    if (list.length > max) list.length = max;
  };
  const dropId = (list, id) => { const i = list.indexOf(num(id)); if (i > -1) list.splice(i, 1); };

  const owned = new Set(split(ctx.owned).map(num).filter(Boolean));
  const ownedArtists = new Set(split(ctx.ownedArtists).map(norm));
  const inCart = new Set(split(ctx.cart).map(num).filter(Boolean));
  const currentId = ctx.product ? num(ctx.product.id) : 0;

  /* ---------- features and scoring ---------- */
  const artistFromTitle = (t) => {
    const part = String(t || '').split('|')[1];
    return part ? part.split(' - ')[0].trim() : '';
  };
  const feats = (p) => {
    const f = {};
    const add = (prefix, label, w) => {
      const k = prefix + norm(label);
      if (k.length > 2 && !(f[k] && f[k][0] >= w)) f[k] = [w, String(label).trim()];
    };
    let artists = p.artists || [];
    if (!artists.length) { const a = artistFromTitle(p.title); if (a) artists = [a]; }
    const an = artists.map(norm);
    artists.forEach((a) => add('a:', a, 1));
    (p.genres || []).forEach((g) => add('g:', g, 0.6));
    if (p.type) add('t:', p.type, 0.25);
    (p.tags || []).forEach((t) => { if (!JUNK.test(t) && an.indexOf(norm(t)) === -1) add('x:', t, 0.4); });
    return f;
  };
  const bump = (p, amount) => {
    if (!p) return;
    const f = feats(p);
    Object.keys(f).forEach((k) => {
      S.w[k] = Math.max(-6, Math.min(40, (S.w[k] || 0) + f[k][0] * amount));
      if (!S.labels[k]) S.labels[k] = f[k][1];
    });
    save();
  };
  const score = (p) => {
    const f = feats(p);
    let s = 0;
    Object.keys(f).forEach((k) => {
      s += (S.w[k] || 0) * f[k][0];
      if (k.charAt(0) === 'a' && ownedArtists.has(k.slice(2))) s += 4;
    });
    if (S.seen.indexOf(p.id) > -1) s -= 1.5;
    if (S.viewed.indexOf(p.id) > -1) s -= 2.5;
    // A little randomness keeps every visit feeling fresh.
    return s + (p.boost || 0) + Math.random() * 0.9;
  };
  const primary = (p) => Object.keys(feats(p)).find((k) => k.charAt(0) === 'a') || 'id:' + p.id;
  const eligible = (p) => p.id && p.id !== currentId && !owned.has(p.id) && !inCart.has(p.id) && S.hidden.indexOf(p.id) === -1;
  // Rank by score, but cap each artist so rows never become one artist wall.
  // Returns the capped picks first, then the overflow. The capped picks alone are on .capped.
  const rank = (list, cap) => {
    const scored = list.map((p) => ({ p, s: score(p) })).sort((a, b) => b.s - a.s);
    const out = [];
    const extra = [];
    const per = {};
    scored.forEach(({ p }) => {
      const k = primary(p);
      per[k] = (per[k] || 0) + 1;
      (per[k] <= cap ? out : extra).push(p);
    });
    const all = out.concat(extra);
    all.capped = out;
    return all;
  };
  const merge = (lists) => {
    const m = new Map();
    lists.forEach((l) => (l || []).forEach((p) => { if (p && p.id && !m.has(p.id)) m.set(p.id, p); }));
    return Array.from(m.values());
  };
  const top = (prefixes, n) => Object.keys(S.w)
    .filter((k) => prefixes.indexOf(k.slice(0, 2)) > -1 && S.w[k] > 0.5)
    .sort((a, b) => S.w[b] - S.w[a])
    .slice(0, n);
  const topArtists = (n) => {
    const names = top(['a:'], n).map((k) => S.labels[k] || k.slice(2));
    const lower = names.map(norm);
    ownedArtists.forEach((a) => { if (names.length < n && a && lower.indexOf(a) === -1) { names.push(a); lower.push(a); } });
    return names;
  };
  const hasSignals = () => top(['a:', 'g:', 'x:'], 1).length > 0 || owned.size > 0;

  /* ---------- fetching theme-rendered cards ---------- */
  const fromEl = (el) => ({
    id: num(el.getAttribute('data-pid')),
    title: el.getAttribute('data-title') || '',
    type: el.getAttribute('data-type') || '',
    artists: split(el.getAttribute('data-artists')),
    genres: split(el.getAttribute('data-genres')),
    tags: split(el.getAttribute('data-tags')),
    el
  });
  const cache = new Map();
  const fetchItems = (url) => {
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) { const empty = []; empty.more = false; return empty; }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = Array.from(doc.querySelectorAll('.vae-item')).map(fromEl);
          const flag = doc.querySelector('[data-vae-more]');
          items.more = !!flag && flag.getAttribute('data-vae-more') === '1';
          return items;
        })
        .catch(() => { const empty = []; empty.more = false; return empty; }));
    }
    return cache.get(url);
  };
  const sep = (u) => (u.indexOf('?') > -1 ? '&' : '?');
  const U = {
    recs: (id, intent, limit) => `${ctx.recsUrl}?product_id=${id}&limit=${limit || 10}&intent=${intent || 'related'}&section_id=vae-cards`,
    search: (q) => `${ctx.searchUrl}?q=${encodeURIComponent(q)}&type=product&options%5Bunavailable_products%5D=hide&section_id=vae-cards`,
    ids: (ids) => U.search(ids.map((i) => 'id:' + i).join(' OR ')),
    coll: (base, sort, page) => `${base}${sep(base)}${sort ? 'sort_by=' + sort + '&' : ''}filter.v.availability=1&page=${page || 1}&section_id=vae-cards`
  };
  const nodeFor = (p) => document.importNode(p.el, true);

  const personalPool = async (o) => {
    const urls = [];
    S.viewed.filter((id) => id !== currentId).slice(0, o.viewed).forEach((id) => urls.push(U.recs(id, 'related', 10)));
    S.liked.slice(0, o.liked).forEach((id) => urls.push(U.recs(id, 'related', 10)));
    topArtists(o.artists).forEach((a) => urls.push(U.search(a)));
    top(['g:', 'x:'], o.tags).forEach((k) => urls.push(U.search(S.labels[k] || k.slice(2))));
    if (urls.length < 2) Array.from(owned).slice(0, 3).forEach((id) => urls.push(U.recs(id, 'related', 10)));
    return merge(await Promise.all(urls.map(fetchItems)));
  };

  /* ---------- signals ---------- */
  let productItem = null;
  if (currentId && ctx.product) {
    productItem = {
      id: currentId,
      title: ctx.product.title || '',
      type: ctx.product.type || '',
      artists: split(ctx.product.artists),
      genres: split(ctx.product.genres),
      tags: split(ctx.product.tags)
    };
    if (S.viewed[0] !== currentId) bump(productItem, 2);
    pushId(S.viewed, currentId, 40);
    S.titles[currentId] = productItem.title;
    save();
    // A long look counts as extra interest.
    setTimeout(() => { if (!document.hidden) bump(productItem, 1); }, 20000);
  }
  document.addEventListener('submit', (e) => {
    const form = e.target;
    const action = (form && form.getAttribute && form.getAttribute('action')) || '';
    if (action.indexOf('/cart/add') === -1) return;
    const item = form.closest('.vae-item');
    bump(item ? fromEl(item) : productItem, 5);
  }, true);
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('.vae-item a[href*="/products/"]');
    if (link) bump(fromEl(link.closest('.vae-item')), 1);
  }, true);

  /* ---------- shelves ---------- */
  const shortTitle = (t) => String(t || '').split('|')[0].trim();
  const runShelf = async (sec) => {
    const mode = sec.getAttribute('data-mode');
    const limit = num(sec.getAttribute('data-limit')) || 10;
    const list = sec.querySelector('[data-vae-list]');
    const head = sec.querySelector('[data-vae-heading]');
    let items = [];
    let heading = '';
    let usedFallback = false;
    try {
      if (mode === 'because_viewed') {
        const id = S.viewed.find((i) => i !== currentId);
        if (id) {
          items = await fetchItems(U.recs(id, 'related', 10));
          if (head && S.titles[id]) heading = (head.getAttribute('data-template') || '').replace('[title]', shortTitle(S.titles[id]));
        }
      } else if (mode === 'artists') {
        const names = topArtists(4);
        if (names.length) {
          const keys = names.map((n) => 'a:' + norm(n));
          items = merge(await Promise.all(names.map((n) => fetchItems(U.search(n)))))
            .filter((p) => { const f = feats(p); return keys.some((k) => f[k]); });
        }
      } else if (mode === 'complete_setup') {
        const ids = currentId ? [currentId] : Array.from(inCart).slice(0, 3);
        if (ids.length) {
          items = merge(await Promise.all(ids.map((i) => fetchItems(U.recs(i, 'complementary', 10)))));
          if (items.length < 4 && sec.getAttribute('data-related-fallback') === 'true') {
            items = merge([items].concat(await Promise.all(ids.map((i) => fetchItems(U.recs(i, 'related', 10))))));
          }
        }
      } else if (hasSignals()) {
        items = await personalPool({ viewed: 3, liked: 2, artists: 3, tags: 2 });
      }
      const ranked = rank(items.filter(eligible), mode === 'for_you' ? 3 : 2);
      items = ranked.slice(0, limit);
      // Blended rows stay varied: if taste only covers one or two artists, top up with
      // bestsellers ranked by the same taste before repeating an artist.
      if (mode === 'for_you' && ranked.capped.length && ranked.capped.length < limit) {
        const known = new Set(ranked.capped.map((p) => p.id));
        const best = rank((await fetchItems(U.coll(ctx.allUrl, 'best-selling', 1))).filter((p) => eligible(p) && !known.has(p.id)), 1);
        items = merge([ranked.capped, best.capped, ranked]).slice(0, limit);
      }
      const fallback = sec.getAttribute('data-fallback');
      if (items.length < 4 && fallback) {
        const fb = (await fetchItems(U.coll(fallback, '', 1))).filter(eligible);
        items = merge([items, rank(fb, 3)]).slice(0, limit);
        usedFallback = true;
      }
    } catch (e) {
      items = [];
    }
    if (items.length < 4) {
      if (window.Shopify && window.Shopify.designMode) {
        list.innerHTML = '<p class="vae-shelf__note">' + esc(sec.getAttribute('data-editor-note')) + '</p>';
        sec.classList.add('is-ready');
      } else {
        sec.remove();
      }
      return;
    }
    if (head) {
      const cold = head.getAttribute('data-cold');
      if (heading) head.textContent = heading;
      else if (usedFallback && cold && !hasSignals()) head.textContent = cold;
    }
    const frag = document.createDocumentFragment();
    items.forEach((p) => frag.appendChild(nodeFor(p)));
    list.innerHTML = '';
    list.appendChild(frag);
    sec.classList.add('is-ready');
  };

  /* ---------- For You feed ---------- */
  const runFeed = (root) => {
    if (root.getAttribute('data-vae-ready')) return;
    root.setAttribute('data-vae-ready', '1');
    const d = root.dataset;
    const grid = root.querySelector('[data-vae-grid]');
    const sentinel = root.querySelector('[data-vae-sentinel]');
    const chips = root.querySelector('[data-vae-chips]');
    const cold = root.querySelector('[data-vae-cold]');
    const endEl = root.querySelector('[data-vae-end]');
    const savedBtn = root.querySelector('[data-vae-saved-toggle]');
    const savedWrap = root.querySelector('[data-vae-saved]');
    const savedGrid = root.querySelector('[data-vae-saved-grid]');
    const savedCount = root.querySelector('[data-vae-saved-count]');
    const resetBtn = root.querySelector('[data-vae-reset]');
    if (!grid || !sentinel) return;

    const batch = Math.max(4, num(d.batch) || 12);
    const exploreShare = Math.min(0.6, Math.max(0.1, (num(d.explore) || 25) / 100));
    const pool = new Map();
    const byId = new Map();
    const shown = new Set();
    const pages = { 'created-descending': 1, 'best-selling': 1 };
    const exhausted = { 'created-descending': false, 'best-selling': false };
    let loading = false;
    let done = false;
    let personalLoaded = false;

    const addToPool = (items, extra) => (items || []).forEach((p) => {
      byId.set(p.id, p);
      const existing = pool.get(p.id);
      if (existing) {
        if (extra && extra.explore === false) existing.explore = false;
        if (extra && extra.boost) existing.boost = Math.max(existing.boost || 0, extra.boost);
        return;
      }
      if (extra) Object.assign(p, extra);
      pool.set(p.id, p);
    });

    const refillExplore = async () => {
      const sorts = Object.keys(pages).filter((s) => !exhausted[s]);
      if (!sorts.length) return false;
      sorts.sort((a, b) => pages[a] - pages[b]);
      const sort = sorts[0];
      const items = await fetchItems(U.coll(ctx.allUrl, sort, pages[sort]));
      pages[sort] += 1;
      if (!items.more || pages[sort] > 200) exhausted[sort] = true;
      addToPool(items, { explore: true });
      return true;
    };

    const pick = () => {
      const avail = Array.from(pool.values()).filter((p) => !shown.has(p.id) && eligible(p));
      const personal = rank(avail.filter((p) => !p.explore), 3);
      const explore = rank(avail.filter((p) => p.explore), 2);
      const nP = hasSignals() ? Math.round(batch * (1 - exploreShare)) : 0;
      const out = personal.slice(0, nP);
      let ei = 0;
      while (out.length < batch && ei < explore.length) out.push(explore[ei++]);
      let pi = nP;
      while (out.length < batch && pi < personal.length) out.push(personal[pi++]);
      // Sprinkle discovery between personal picks instead of dumping it at the end.
      const pers = out.filter((p) => !p.explore);
      const exp = out.filter((p) => p.explore);
      const mixed = [];
      while (pers.length || exp.length) {
        for (let i = 0; i < 3 && pers.length; i++) mixed.push(pers.shift());
        if (exp.length) mixed.push(exp.shift());
      }
      return { list: mixed, remaining: avail.length - mixed.length };
    };

    const tile = (p) => {
      const wrap = document.createElement('div');
      wrap.className = 'vae-tile';
      wrap.setAttribute('data-pid', p.id);
      wrap.appendChild(nodeFor(p));
      const bar = document.createElement('div');
      bar.className = 'vae-tile__actions';
      const liked = S.liked.indexOf(p.id) > -1;
      bar.innerHTML = '<button type="button" class="vae-btn vae-like" aria-pressed="' + liked + '" aria-label="' + esc(d.likeLabel) + '">' + HEART + '</button>' +
        '<button type="button" class="vae-btn vae-hide" aria-label="' + esc(d.hideLabel) + '">' + CROSS + '</button>';
      wrap.appendChild(bar);
      return wrap;
    };

    const render = (list) => {
      const frag = document.createDocumentFragment();
      list.forEach((p, i) => {
        shown.add(p.id);
        pushId(S.seen, p.id, 500);
        const t = tile(p);
        t.style.animationDelay = Math.min(i * 40, 400) + 'ms';
        frag.appendChild(t);
      });
      grid.appendChild(frag);
      save();
    };

    const near = () => sentinel.getBoundingClientRect().top < window.innerHeight + 900;

    const loadMore = async () => {
      if (loading || done) return;
      loading = true;
      let rendered = false;
      try {
        if (!personalLoaded) {
          personalLoaded = true;
          if (hasSignals()) addToPool(await personalPool({ viewed: 5, liked: 4, artists: 5, tags: 3 }), { explore: false });
        }
        let res = pick();
        let tries = 0;
        while ((res.list.length < batch || res.remaining < batch) && tries < 4) {
          const more = await refillExplore();
          if (!more) break;
          tries += 1;
          res = pick();
        }
        if (!res.list.length) {
          done = true;
          if (endEl) endEl.hidden = false;
          sentinel.hidden = true;
        } else {
          render(res.list);
          rendered = true;
        }
      } catch (e) {
        /* network hiccup: the observer will try again on the next scroll */
      } finally {
        loading = false;
      }
      // Keep filling while the bottom is still in view, but never run away without the shopper scrolling.
      if (rendered && !done && near() && chained < 3) {
        chained += 1;
        requestAnimationFrame(loadMore);
      }
    };
    let chained = 0;
    window.addEventListener('scroll', () => { chained = 0; }, { passive: true });

    /* toast */
    let toastEl = null;
    let toastTimer = 0;
    const toast = (msg, actionLabel, onUndo) => {
      if (!msg) return;
      if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.className = 'vae-toast';
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastEl);
      }
      toastEl.innerHTML = '<span>' + esc(msg) + '</span>' + (actionLabel && onUndo ? '<button type="button">' + esc(actionLabel) + '</button>' : '');
      const b = toastEl.querySelector('button');
      if (b) b.addEventListener('click', () => { onUndo(); toastEl.classList.remove('is-on'); });
      toastEl.classList.add('is-on');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 4500);
    };

    /* taste chips */
    const paintChips = () => {
      if (!chips) return;
      const keys = top(['a:', 'g:', 'x:'], 6);
      if (cold) cold.hidden = keys.length > 0 || owned.size > 0;
      chips.innerHTML = keys.length
        ? '<span class="vae-chips__label">' + esc(d.tasteLabel) + '</span>' + keys.map((k) => {
          const label = S.labels[k] || k.slice(2);
          return '<button type="button" class="vae-chip" data-key="' + esc(k) + '" aria-label="' + esc((d.removeLabel || '') + ' ' + label) + '">' + esc(label) + '<span aria-hidden="true">&times;</span></button>';
        }).join('')
        : '';
    };
    const paintCount = () => { if (savedCount) savedCount.textContent = String(S.liked.length); };

    if (chips) {
      chips.addEventListener('click', (e) => {
        const c = e.target.closest('.vae-chip');
        if (!c) return;
        const k = c.getAttribute('data-key');
        const before = S.w[k];
        S.w[k] = -3;
        save();
        paintChips();
        toast(d.hiddenToast, d.undoLabel, () => { S.w[k] = before; save(); paintChips(); });
      });
    }

    const toggleLike = (p) => {
      const on = S.liked.indexOf(p.id) === -1;
      if (on) {
        pushId(S.liked, p.id, 200);
        S.titles[p.id] = p.title;
        bump(p, 6);
        // Learn instantly: pull in neighbours of what was just saved.
        fetchItems(U.recs(p.id, 'related', 10)).then((items) => addToPool(items, { explore: false, boost: 3 }));
        const artist = (p.artists && p.artists[0]) || artistFromTitle(p.title);
        if (artist) fetchItems(U.search(artist)).then((items) => addToPool(items, { explore: false, boost: 1 }));
      } else {
        dropId(S.liked, p.id);
        bump(p, -6);
      }
      root.querySelectorAll('.vae-tile[data-pid="' + p.id + '"] .vae-like').forEach((b) => {
        b.setAttribute('aria-pressed', String(on));
        b.classList.remove('is-pop');
        if (on) { void b.offsetWidth; b.classList.add('is-pop'); }
      });
      save();
      paintChips();
      paintCount();
    };

    const hide = (p, tileEl) => {
      pushId(S.hidden, p.id, 400);
      bump(p, -4);
      paintChips();
      const parent = tileEl.parentNode;
      const next = tileEl.nextSibling;
      tileEl.classList.add('is-leaving');
      setTimeout(() => { if (tileEl.classList.contains('is-leaving')) tileEl.remove(); }, 260);
      toast(d.hiddenToast, d.undoLabel, () => {
        dropId(S.hidden, p.id);
        bump(p, 4);
        tileEl.classList.remove('is-leaving');
        if (!tileEl.parentNode && parent) parent.insertBefore(tileEl, next && next.parentNode === parent ? next : null);
        paintChips();
      });
    };

    const onAction = (e) => {
      const btn = e.target.closest('.vae-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const tileEl = btn.closest('.vae-tile');
      const p = tileEl && byId.get(num(tileEl.getAttribute('data-pid')));
      if (!p) return;
      if (btn.classList.contains('vae-like')) toggleLike(p);
      else hide(p, tileEl);
    };
    grid.addEventListener('click', onAction);
    if (savedGrid) savedGrid.addEventListener('click', onAction);

    if (savedBtn && savedWrap && savedGrid) {
      savedBtn.addEventListener('click', async () => {
        const open = savedWrap.hidden;
        savedWrap.hidden = !open;
        savedBtn.setAttribute('aria-expanded', String(open));
        if (!open) return;
        savedGrid.innerHTML = '';
        if (!S.liked.length) {
          savedGrid.innerHTML = '<p class="vae-feed__empty">' + esc(d.savedEmpty) + '</p>';
          return;
        }
        const order = S.liked.slice(0, 48);
        const items = (await fetchItems(U.ids(order))).slice().sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
        items.forEach((p) => byId.set(p.id, p));
        const frag = document.createDocumentFragment();
        items.forEach((p) => frag.appendChild(tile(p)));
        savedGrid.appendChild(frag);
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        clearTimeout(saveTimer);
        saveTimer = 0;
        S = blank();
        try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
        window.location.reload();
      });
    }

    paintChips();
    paintCount();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => { if (entries.some((x) => x.isIntersecting)) loadMore(); }, { rootMargin: '900px 0px' }).observe(sentinel);
    } else {
      window.addEventListener('scroll', () => { if (near()) loadMore(); }, { passive: true });
    }
    loadMore();
  };

  /* ---------- boot ---------- */
  const boot = (scope) => {
    const shelves = scope.querySelectorAll('[data-vae-shelf]');
    if (shelves.length) {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach((en) => { if (en.isIntersecting) { obs.unobserve(en.target); runShelf(en.target); } });
        }, { rootMargin: '600px 0px' });
        shelves.forEach((s) => io.observe(s));
      } else {
        shelves.forEach(runShelf);
      }
    }
    scope.querySelectorAll('[data-vae-feed]').forEach(runFeed);
  };
  boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
